import { useMemo } from "react";
import t from "../../../../../../i18n";
import { NO_SYSTEM } from "../../useLossesBySystem";
import { useTranslations } from "../../../../../../api/useTranslations";
import seriesColors from "../../../../Chart/series/seriesColors";

const UN_EXPLICATED_WIDGET_LR_ID = "UN_EXPLICATED_WIDGET_LR_ID";
const INTERNAL_LOSSES_WIDGET_LR_ID = "INTERNAL_LOSSES_WIDGET_LR_ID";
const OTHER_SYSTEM_LOSSES_WIDGET_LR_ID = "OTHER_SYSTEM_LOSSES_WIDGET_LR_ID";
const SYSTEM_LOSSES_WIDGET_LR_ID = "SYSTEM_LOSSES_WIDGET_LR_ID";

const lossesRepartitionColors = [
  "#A99211",
  "#009D9A",
  "#6929C4",
  "#7B7F8B",
  "#D2D4DD",
  seriesColors.fadeSparklineArea,
];
const lossesRepartitionTextColors = [
  "#ffffff",
  "#ffffff",
  "#ffffff",
  "#ffffff",
  "#394959",
  "#394959",
];
export function getColorLossesRepartition(index: number) {
  const color = lossesRepartitionColors[index];
  if (color === undefined) throw new Error("Invalid index");
  return color;
}
export function getTextColorLossesRepartition(index: number) {
  const textColor = lossesRepartitionTextColors[index];
  if (textColor === undefined) throw new Error("Invalid index");
  return textColor;
}

export const getPodiumSystems = (
  systems: {
    system: string;
    averageLosses: number;
  }[],
) => systems.filter((_, index) => index < 3 && _.system !== NO_SYSTEM);
export const getOtherSystems = (
  systems: {
    system: string;
    averageLosses: number;
  }[],
) => systems.filter((_, index) => index >= 3 || _.system === NO_SYSTEM);

const useSeries = ({
  meanLossesBySystem,
  meanExplicatedLosses,
  meanInternalLosses,
}: {
  meanLossesBySystem: {
    system: string;
    averageLosses: number;
  }[];
  meanExplicatedLosses: number;
  meanInternalLosses: {
    meanLosses: number;
    meanMeasure: number;
  } | null;
}) => {
  const translations = useTranslations();
  const majorLossesSystemSeries: Highcharts.SeriesBarOptions[] = useMemo(() => {
    const series: Highcharts.SeriesBarOptions[] = [];
    const majorLossesSystem = getPodiumSystems(meanLossesBySystem).sort(
      (a, b) => a.averageLosses - b.averageLosses,
    );

    if (majorLossesSystem.length > 0) {
      for (const [index, { system, averageLosses }] of majorLossesSystem.entries()) {
        const color = getColorLossesRepartition(index);
        series.push({
          id: system + "_" + SYSTEM_LOSSES_WIDGET_LR_ID,
          name: system,
          type: "bar",
          replicateInNavigator: false,
          data: [
            {
              y: averageLosses,
              color,
              borderWidth: 0,
              dataLabels: {
                style: {
                  textOutline: "none",
                  color: getTextColorLossesRepartition(index),
                },
              },
            },
            {
              y: null,
              color,
            },
          ],
        });
      }
    }
    return series;
  }, [meanLossesBySystem]);

  const otherLossesSystemSerie: Highcharts.SeriesBarOptions | null = useMemo(() => {
    const otherLossesSystem = getOtherSystems(meanLossesBySystem);
    if (otherLossesSystem.length > 0) {
      return {
        id: OTHER_SYSTEM_LOSSES_WIDGET_LR_ID,
        name: t("project.fault_events.charts.losses_repartition.label.other_systems"),
        type: "bar",
        replicateInNavigator: false,
        data: [
          {
            y: otherLossesSystem.reduce((acc, value) => acc + value.averageLosses, 0),
            color: getColorLossesRepartition(3),
            borderWidth: 0,
            dataLabels: {
              style: {
                textOutline: "none",
                color: getTextColorLossesRepartition(3),
              },
            },
          },
          {
            y: null,
            color: getColorLossesRepartition(3),
          },
        ],
      };
    }
    return null;
  }, [meanLossesBySystem]);

  const unExplicatedLossesSerie: Highcharts.SeriesBarOptions | null = useMemo(() => {
    const unExplicatedLosses = meanInternalLosses
      ? meanInternalLosses.meanLosses - meanExplicatedLosses
      : null;
    if (unExplicatedLosses === null) return null;

    return {
      id: UN_EXPLICATED_WIDGET_LR_ID,
      name: t(
        "project.fault_events.charts.losses_repartition.label.unexplained_losses",
        translations,
      ),
      type: "bar",
      data: [
        {
          y: unExplicatedLosses,
          color: getColorLossesRepartition(4),
          borderWidth: 0,
          dataLabels: {
            style: {
              textOutline: "none",
              color: getTextColorLossesRepartition(4),
            },
          },
        },
        {
          y: null,
          color: "#394959",
        },
      ],
      replicateInNavigator: false,
    };
  }, [meanInternalLosses, meanExplicatedLosses, translations]);

  const internalLossesSerie: Highcharts.SeriesBarOptions | null = useMemo(() => {
    if (meanInternalLosses === null) return null;

    return {
      id: INTERNAL_LOSSES_WIDGET_LR_ID,
      name: t("project.fault_events.charts.losses_repartition.label.internal_losses"),
      type: "bar",
      replicateInNavigator: false,
      data: [
        {
          y: null,
          color: getColorLossesRepartition(5),
        },
        {
          y: meanInternalLosses.meanLosses,
          color: getColorLossesRepartition(5),
          borderWidth: 0,
          dataLabels: {
            style: {
              textOutline: "none",
              color: getTextColorLossesRepartition(5),
            },
          },
        },
      ],
    };
  }, [meanInternalLosses]);

  return useMemo(
    () =>
      [
        ...majorLossesSystemSeries,
        otherLossesSystemSerie,
        unExplicatedLossesSerie,
        internalLossesSerie,
      ].filter(f => f !== null),
    [majorLossesSystemSeries, otherLossesSystemSerie, unExplicatedLossesSerie, internalLossesSerie],
  );
};

export default useSeries;
